import React, { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import api from "../services/api";

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
};

const formatDateLabel = (value, options = {}) => {
  if (!value) return "";
  const date = typeof value === "string" || typeof value === "number" ? new Date(value) : value;
  if (Number.isNaN(date?.valueOf())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(options || {}),
  });
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const datum = payload[0]?.payload || {};
  const header = datum.fullDate || datum.date || "";

  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow">
      <p className="font-semibold text-slate-700">{header}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} className="mt-1 flex items-center gap-2 text-slate-600">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color || "#0f766e" }} />
          <span className="font-medium text-slate-700">{entry.name}:</span>
          <span>{formatCurrency(entry.value)}</span>
        </p>
      ))}
    </div>
  );
};

const Performance = () => {
  const [investments, setInvestments] = useState([]);
  const [dailyPerformance, setDailyPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        // Loading investments accrues today's values and refreshes snapshots.
        // Read the daily series afterwards so it cannot race against that refresh.
        const investmentData = await api.getMyInvestments();
        const dailyData = await api.getDailyInvestmentPerformance().catch((err) => {
          console.warn("performance.daily-trend", err);
          return [];
        });
        setInvestments(Array.isArray(investmentData) ? investmentData : []);
        setDailyPerformance(Array.isArray(dailyData) ? dailyData : []);
      } catch (err) {
        console.error("performance", err);
        setError(err.message || "Unable to load performance data.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const dailySeries = useMemo(() => {
    if (!Array.isArray(dailyPerformance) || !dailyPerformance.length) {
      return [];
    }
    return dailyPerformance
      .map((item) => {
        const rawDate = item?.date;
        const dateObj = rawDate ? new Date(rawDate) : null;
        const timestamp = dateObj && !Number.isNaN(dateObj.valueOf()) ? dateObj.getTime() : null;
        const principal = Number(item?.principal ?? 0);
        const value = Number(item?.value ?? 0);
        const gainValue = Number(item?.gain ?? item?.accrued_interest ?? 0);
        return {
          date: rawDate,
          timestamp,
          shortDate: dateObj ? formatDateLabel(dateObj) : rawDate || "",
          fullDate: dateObj ? formatDateLabel(dateObj, { year: "numeric" }) : rawDate || "",
          principal,
          value,
          gain: gainValue,
          gainPct: Number(item?.gain_pct ?? 0),
          investmentCount: Number(item?.investment_count ?? item?.investmentCount ?? 0),
        };
      })
      .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  }, [dailyPerformance]);

  const latestDaily = dailySeries.length ? dailySeries[dailySeries.length - 1] : null;
  const previousDaily = dailySeries.length > 1 ? dailySeries[dailySeries.length - 2] : null;

  const metrics = useMemo(() => {
    const fallback = {
      totalGain: 0,
      totalInvested: 0,
      currentValue: 0,
      averageReturn: 0,
      weightedAnnualRate: 0,
      bestPerformer: null,
      worstPerformer: null,
      dailyChange: null,
      dailyGainPct: latestDaily?.gainPct ?? 0,
    };

    if (!investments.length) {
      return {
        ...fallback,
        totalGain: Number(latestDaily?.gain ?? 0),
        totalInvested: Number(latestDaily?.principal ?? 0),
        currentValue: Number(latestDaily?.value ?? 0),
        dailyGainPct: latestDaily?.gainPct ?? fallback.dailyGainPct,
        dailyChange: latestDaily && previousDaily ? latestDaily.gain - previousDaily.gain : null,
      };
    }

    let totalInvested = investments.reduce((sum, inv) => sum + Number(inv.amount || 0), 0);
    let currentValue = investments.reduce((sum, inv) => sum + Number(inv.value || inv.amount || 0), 0);
    let totalGain = currentValue - totalInvested;
    const holdingReturn = (inv) => {
      const principal = Number(inv.principal ?? inv.amount ?? 0);
      const value = Number(inv.current_value ?? inv.value ?? principal);
      return principal > 0 ? ((value - principal) / principal) * 100 : 0;
    };
    const annualRate = (inv) => Number(inv.annual_rate_pct ?? inv.return_pct ?? inv.returnPct ?? 0);

    const activeInvestments = investments.filter(
      (inv) => Number(inv.current_value ?? inv.value ?? inv.principal ?? inv.amount ?? 0) > 0
    );
    const sortedByReturn = activeInvestments
      .map((inv) => ({
        ...inv,
        performanceReturn: holdingReturn(inv),
        annualRate: annualRate(inv),
      }))
      .sort((a, b) => b.performanceReturn - a.performanceReturn);
    const weightedPrincipal = activeInvestments.reduce(
      (sum, inv) => sum + Number(inv.principal ?? inv.amount ?? 0),
      0
    );
    const weightedAnnualRate = weightedPrincipal > 0
      ? activeInvestments.reduce(
          (sum, inv) => sum + Number(inv.principal ?? inv.amount ?? 0) * annualRate(inv),
          0
        ) / weightedPrincipal
      : 0;

    if (latestDaily) {
      if (Number.isFinite(Number(latestDaily.principal))) {
        totalInvested = Number(latestDaily.principal);
      }
      if (Number.isFinite(Number(latestDaily.value))) {
        currentValue = Number(latestDaily.value);
      }
      if (Number.isFinite(Number(latestDaily.gain))) {
        totalGain = Number(latestDaily.gain);
      }
    }

    const averageReturn = totalInvested > 0 ? (totalGain / totalInvested) * 100 : 0;

    return {
      totalGain,
      totalInvested,
      currentValue,
      averageReturn,
      weightedAnnualRate,
      bestPerformer: sortedByReturn[0],
      worstPerformer: sortedByReturn[sortedByReturn.length - 1],
      dailyChange: latestDaily && previousDaily ? latestDaily.gain - previousDaily.gain : null,
      dailyGainPct: latestDaily?.gainPct ?? 0,
    };
  }, [investments, latestDaily, previousDaily]);

  const activeHoldingsCount = latestDaily && Number.isFinite(Number(latestDaily.investmentCount))
    ? Number(latestDaily?.investmentCount)
    : investments.filter(
        (inv) => Number(inv.current_value ?? inv.value ?? inv.principal ?? inv.amount ?? 0) > 0
      ).length;

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="bg-white rounded-lg p-6 shadow">
          <h1 className="text-2xl font-bold text-gray-800">Performance analytics</h1>
          <p className="text-sm text-gray-500 mt-1">
            Understand how your portfolio is progressing across all holdings.
          </p>

          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {loading ? (
          <div className="bg-white rounded-lg p-6 shadow">
            <p className="text-gray-500">Loading performance data...</p>
          </div>
        ) : !investments.length && !dailySeries.length ? (
          <div className="bg-white rounded-lg p-6 shadow">
            <p className="text-gray-500">
              You have not recorded any investments yet. Once you get started we will track daily performance here.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-6 shadow">
                <p className="text-sm text-gray-500">Total gain</p>
                <p className={`text-2xl font-bold ${metrics.totalGain >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {formatCurrency(metrics.totalGain)}
                </p>
                {metrics.dailyChange !== null && (
                  <p className={`mt-1 text-xs font-medium ${metrics.dailyChange >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {metrics.dailyChange >= 0 ? "+" : ""}
                    {formatCurrency(metrics.dailyChange)} vs previous day
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Difference between portfolio value and invested principal.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow">
                <p className="text-sm text-gray-500">Portfolio value</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(metrics.currentValue)}</p>
                <p className="text-xs text-gray-500 mt-2">
                  Across {activeHoldingsCount} investment{activeHoldingsCount === 1 ? "" : "s"} tracked daily.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow">
                <p className="text-sm text-gray-500">Return earned to date</p>
                <p className="text-2xl font-bold text-gray-800">{metrics.averageReturn.toFixed(1)}%</p>
                <p className="text-xs text-gray-500 mt-2">
                  Accrued gain so far. Weighted annual investment rate: {metrics.weightedAnnualRate.toFixed(1)}%.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Daily performance trend</h2>
                  <p className="text-sm text-gray-500">
                    Tracks invested principal, total value, and accrued gains for each day.
                  </p>
                </div>
                {latestDaily && (
                  <div className="text-sm text-right text-gray-600">
                    <p className="text-xs uppercase tracking-wide text-gray-400">Latest snapshot</p>
                    <p className="text-sm font-semibold text-gray-700">{latestDaily.fullDate}</p>
                    <p className={`text-base font-semibold ${latestDaily.gain >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      Gain {formatCurrency(latestDaily.gain)} ({latestDaily.gainPct.toFixed(2)}%)
                    </p>
                    <p className="text-xs text-gray-500">
                      Value {formatCurrency(latestDaily.value)} · Invested {formatCurrency(latestDaily.principal)}
                    </p>
                  </div>
                )}
              </div>
              <div className="mt-6 h-72 w-full">
                {dailySeries.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dailySeries} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="valueArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0f766e" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#0f766e" stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="principalArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2563eb" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#2563eb" stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="gainArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f97316" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#f97316" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" />
                      <XAxis dataKey="shortDate" tick={{ fontSize: 12, fill: "#475569" }} stroke="#cbd5f5" />
                      <YAxis
                        tick={{ fontSize: 12, fill: "#475569" }}
                        tickFormatter={(value) => formatCurrency(value).replace("NGN", "₦")}
                        width={110}
                      />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="value"
                        name="Total value"
                        stroke="#0f766e"
                        fill="url(#valueArea)"
                        strokeWidth={2}
                        activeDot={{ r: 4 }}
                      />
                      <Area
                        type="monotone"
                        dataKey="principal"
                        name="Invested principal"
                        stroke="#2563eb"
                        fill="url(#principalArea)"
                        strokeWidth={2}
                      />
                      <Area
                        type="monotone"
                        dataKey="gain"
                        name="Accrued gain"
                        stroke="#f97316"
                        fill="url(#gainArea)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-gray-500">
                    Daily tracking will appear once we accrue at least one snapshot for your investments.
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-6 shadow">
                <h2 className="text-lg font-semibold text-gray-800">Best performer</h2>
                {metrics.bestPerformer ? (
                  <div className="mt-3 space-y-1 text-sm text-gray-600">
                    <p className="text-base font-semibold text-gray-900">{metrics.bestPerformer.name}</p>
                    <p>Type: {metrics.bestPerformer.type || "-"}</p>
                    <p>Earned to date: {Number(metrics.bestPerformer.performanceReturn || 0).toFixed(1)}%</p>
                    <p>Annual rate: {Number(metrics.bestPerformer.annualRate || 0).toFixed(1)}%</p>
                    <p>Value: {formatCurrency(metrics.bestPerformer.value || metrics.bestPerformer.amount)}</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No data.</p>
                )}
              </div>
              <div className="bg-white rounded-lg p-6 shadow">
                <h2 className="text-lg font-semibold text-gray-800">Needs attention</h2>
                {metrics.worstPerformer ? (
                  <div className="mt-3 space-y-1 text-sm text-gray-600">
                    <p className="text-base font-semibold text-gray-900">{metrics.worstPerformer.name}</p>
                    <p>Type: {metrics.worstPerformer.type || "-"}</p>
                    <p>Earned to date: {Number(metrics.worstPerformer.performanceReturn || 0).toFixed(1)}%</p>
                    <p>Annual rate: {Number(metrics.worstPerformer.annualRate || 0).toFixed(1)}%</p>
                    <p>Value: {formatCurrency(metrics.worstPerformer.value || metrics.worstPerformer.amount)}</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No data.</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Performance;
